/**
 * A share link viewed anonymously.
 *
 * The token is held in a module variable rather than passed down through props
 * because of where it has to be applied: the API client, several layers below
 * every component that would otherwise have to thread it through. The demo uses
 * the same shape, and for the same reason — the alternative is a second copy of
 * every page that differs only in which URL it fetches.
 */
let activeToken: string | null = null

/** Sets the token for the current shared view. */
export function setShareToken(token: string | null): void {
  activeToken = token
}

/** The token currently being viewed, if any. */
export function shareToken(): string | null {
  return activeToken
}

/**
 * The placeholder ids a shared view uses in place of a project and profile.
 *
 * The pages build their requests from route parameters, and a share has no
 * project. These stand in so the existing pages work unchanged, and the client
 * rewrites the resulting paths onto the share API.
 */
export const SHARE_PROJECT_ID = 'shared'
export const SHARE_PROFILE_ID = 'profile'

/**
 * rewrite maps a normal profile request onto the share API.
 *
 * Returns null when the path is not one a share can answer — which is most of
 * them. A shared visitor's page must not be able to reach the project, the
 * profile list, or a second profile, and the safest way to guarantee that is
 * for anything unrecognised to fail rather than fall through to the
 * authenticated API without a token.
 */
export function rewrite(path: string): string | null {
  const token = activeToken
  if (!token) {
    return null
  }

  const prefix = `/projects/${SHARE_PROJECT_ID}/profiles/${SHARE_PROFILE_ID}`
  if (!path.startsWith(prefix)) {
    return null
  }

  const rest = path.slice(prefix.length)
  const [base, search] = rest.split('?')

  // Only the reads a shared profile page performs.
  const allowed = ['', '/functions', '/flame', '/findings', '/relations']
  if (!allowed.includes(base)) {
    return null
  }

  return `/shared/${token}${base}${search ? `?${search}` : ''}`
}

/** True when a path belongs to a shared view. */
export function isSharePath(path: string): boolean {
  return (
    activeToken !== null &&
    path.startsWith(`/projects/${SHARE_PROJECT_ID}/profiles/${SHARE_PROFILE_ID}`)
  )
}
